import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  ALL_REPORT_DELIVERY_STATUSES,
  ALL_REPORT_STATUSES,
  type ReportDeliveryStatus,
  type ReportStatus,
} from '@lims/shared-types';
import {
  clientsApi,
  projectsApi,
  reportsApi,
  workOrdersApi,
  type ReportRegisterItemView,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  Alert,
  DataTable,
  DateField,
  EmptyState,
  FilterBar,
  PageHeader,
  SearchField,
  SelectField,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import {
  DeliveryStatusBadge,
  REPORTS_QUERY_KEY,
  ReportNumber,
  ReportStatusBadge,
  TestTitles,
} from './report-parts.js';
import { RegisterPanel } from './register-panel.js';

/**
 * THE REPORT REGISTER — Phase 10.
 *
 * ===========================================================================
 * ISSUED IS ONE FACT. DELIVERED IS ANOTHER (CLAUDE.md section 8, D121).
 * ===========================================================================
 *
 * Every row carries the report's status AND its delivery state, side by side,
 * and each has its own filter. A report issued and valid but never handed over
 * reads "Issued and valid" next to "Not released" — never one word that could
 * be taken for both.
 *
 * The indicators beside a row — a draft in preparation, a payment hold, a
 * result that needs a decision — are facts for STAFF. None of them changes the
 * report's status, and a payment hold is never disclosed outside this product.
 *
 * Reading needs `reports.view`, re-checked by the server on every request.
 */
export function ReportsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();
  const [params] = useSearchParams();

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ReportStatus | null>(null);
  const [deliveryStatus, setDeliveryStatus] = useState<ReportDeliveryStatus | null>(null);
  const [clientId, setClientId] = useState<string | null>(params.get('clientId'));
  const [projectId, setProjectId] = useState<string | null>(params.get('projectId'));
  const [workOrderId, setWorkOrderId] = useState<string | null>(params.get('workOrderId'));
  const [search, setSearch] = useState('');
  const [issuedFrom, setIssuedFrom] = useState<string | null>(null);
  const [issuedTo, setIssuedTo] = useState<string | null>(null);

  const filters = {
    ...(status ? { status } : {}),
    ...(deliveryStatus ? { deliveryStatus } : {}),
    ...(clientId ? { clientId } : {}),
    ...(projectId ? { projectId } : {}),
    ...(workOrderId ? { workOrderId } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(issuedFrom ? { issuedFrom } : {}),
    ...(issuedTo ? { issuedTo } : {}),
  };
  const filtered = Object.keys(filters).length > 0;

  const list = useQuery({
    queryKey: [...REPORTS_QUERY_KEY, 'register', { page, ...filters }],
    queryFn: () => reportsApi.list({ page, ...filters }),
  });

  // Filter choices, each read only by someone who may read that register.
  const clients = useQuery({
    queryKey: ['clients', 'report-filter'],
    queryFn: () => clientsApi.list({ pageSize: 100 }),
    enabled: can(PERMISSIONS.CLIENTS_VIEW),
  });
  const projects = useQuery({
    queryKey: ['projects', 'report-filter', clientId],
    queryFn: () => projectsApi.list({ pageSize: 100, ...(clientId ? { clientId } : {}) }),
    enabled: can(PERMISSIONS.PROJECTS_VIEW),
  });
  const workOrders = useQuery({
    queryKey: ['work-orders', 'report-filter'],
    queryFn: () => workOrdersApi.list({ pageSize: 100 }),
    enabled: can(PERMISSIONS.WORK_ORDERS_VIEW),
  });

  const mayPrepare = can(PERMISSIONS.REPORTS_PREPARE);
  const prepareButton = mayPrepare ? (
    <ActionButton priority="primary" onPress={() => navigate('/reports/new')}>
      {t('reports.prepareAction')}
    </ActionButton>
  ) : null;

  const rows = list.data?.data ?? [];
  const total = list.data?.meta.total ?? 0;
  const registerIsEmpty = !filtered && list.isSuccess && total === 0;

  const resetPage =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setPage(1);
    };

  /*
    Column one carries the number, the version, BOTH states and the
    indicators, with the link that opens the report — so on a 390 px phone,
    where the table scrolls sideways, the row's key facts and its way in are
    the part already on screen.
  */
  const columns: readonly DataColumn<ReportRegisterItemView>[] = [
    {
      key: 'number',
      header: t('reports.columns.number'),
      render: (row) => (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <Link
            to={`/reports/${row.id}`}
            className="measure font-semibold text-primary-700 underline-offset-2 hover:underline"
          >
            <ReportNumber number={row.reportNumber} />
          </Link>
          {row.currentVersionNumber !== null ? (
            <span className="text-caption text-default-600">
              {t('reports.detail.version', {
                number: row.currentVersionNumber,
              })}
            </span>
          ) : null}
          <span className="flex flex-wrap items-center gap-inline">
            <ReportStatusBadge status={row.status} />
            {/* Only a report ever issued has anything to hand over. */}
            {row.currentVersionNumber !== null ? (
              <DeliveryStatusBadge status={row.deliveryStatus} />
            ) : null}
          </span>
          <RowIndicators row={row} />
        </div>
      ),
    },
    {
      key: 'client',
      header: t('reports.columns.client'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-default-800">
            <bdi>{row.client.name ?? t('common.notRecorded')}</bdi>
          </span>
          <span className="block text-caption text-default-600">
            {t('reports.columns.project')}
            {': '}
            <bdi>
              {row.project ? (row.project.name ?? t('common.notRecorded')) : t('common.none')}
            </bdi>
          </span>
        </div>
      ),
    },
    {
      key: 'tests',
      header: t('reports.columns.tests'),
      render: (row) => <TestTitles titles={row.testTitles} />,
    },
    {
      key: 'issued',
      header: t('reports.columns.issuedAt'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-default-800">
            <bdi>{formatDate(row.issuedAt, { locale }) ?? t('common.never')}</bdi>
          </span>
          {row.language ? (
            <span className="block text-caption text-default-600">
              {t(`reportLanguage.${row.language}`)}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'workOrder',
      header: t('reports.columns.workOrder'),
      render: (row) => (
        <span className="measure text-default-700" dir="ltr">
          {row.workOrder.number}
        </span>
      ),
    },
  ];

  const clientOptions = [
    { value: 'all', label: t('common.all') },
    ...(clients.data?.data ?? []).map((client) => ({
      value: client.id,
      label: client.name ?? client.clientNumber,
    })),
  ];
  const projectOptions = [
    { value: 'all', label: t('common.all') },
    ...(projects.data?.data ?? []).map((project) => ({
      value: project.id,
      label: project.name ?? project.reference ?? project.clientNumber,
    })),
  ];
  const workOrderOptions = [
    { value: 'all', label: t('common.all') },
    ...(workOrders.data?.data ?? []).map((order) => ({
      value: order.id,
      label: order.workOrderNumber,
    })),
  ];
  const fromAll = (value: string | null) => (value === null || value === 'all' ? null : value);

  return (
    <>
      <PageHeader
        title={t('reports.title')}
        subtitle={t('reports.subtitle')}
        breadcrumbs={[{ label: t('nav.reports') }]}
        actions={prepareButton}
      />

      <div className="flex flex-col gap-gutter">
        <Alert
          tone="info"
          title={t('reports.issuanceNotDeliveryTitle')}
          description={t('reports.issuanceNotDeliveryBody')}
        />

        <RegisterPanel
          toolbar={
            <FilterBar>
              <SearchField
                label={t('reports.filters.search')}
                placeholder={t('reports.filters.searchPlaceholder')}
                value={search}
                onChange={resetPage(setSearch)}
              />
              <div className="w-full sm:w-56">
                <SelectField
                  label={t('reports.filters.status')}
                  value={status ?? 'all'}
                  onChange={resetPage((value: string | null) =>
                    setStatus(fromAll(value) as ReportStatus | null),
                  )}
                  options={[
                    { value: 'all', label: t('reports.filters.anyStatus') },
                    ...ALL_REPORT_STATUSES.map((value) => ({
                      value,
                      label: t(`status.report.${value}`),
                    })),
                  ]}
                />
              </div>
              <div className="w-full sm:w-56">
                <SelectField
                  label={t('reports.filters.deliveryStatus')}
                  value={deliveryStatus ?? 'all'}
                  onChange={resetPage((value: string | null) =>
                    setDeliveryStatus(fromAll(value) as ReportDeliveryStatus | null),
                  )}
                  options={[
                    { value: 'all', label: t('reports.filters.anyDelivery') },
                    ...ALL_REPORT_DELIVERY_STATUSES.map((value) => ({
                      value,
                      label: t(`status.reportDelivery.${value}`),
                    })),
                  ]}
                />
              </div>
              {can(PERMISSIONS.CLIENTS_VIEW) ? (
                <div className="w-full sm:w-56">
                  <SelectField
                    label={t('reports.filters.client')}
                    value={clientId ?? 'all'}
                    onChange={resetPage((value: string | null) => {
                      setClientId(fromAll(value));
                      setProjectId(null);
                    })}
                    options={clientOptions}
                  />
                </div>
              ) : null}
              {can(PERMISSIONS.PROJECTS_VIEW) ? (
                <div className="w-full sm:w-56">
                  <SelectField
                    label={t('reports.filters.project')}
                    value={projectId ?? 'all'}
                    onChange={resetPage((value: string | null) => setProjectId(fromAll(value)))}
                    options={projectOptions}
                  />
                </div>
              ) : null}
              {can(PERMISSIONS.WORK_ORDERS_VIEW) ? (
                <div className="w-full sm:w-56">
                  <SelectField
                    label={t('reports.filters.workOrder')}
                    value={workOrderId ?? 'all'}
                    onChange={resetPage((value: string | null) => setWorkOrderId(fromAll(value)))}
                    options={workOrderOptions}
                  />
                </div>
              ) : null}
              <div className="w-full sm:w-48">
                <DateField
                  label={t('reports.filters.issuedFrom')}
                  value={issuedFrom}
                  onChange={resetPage(setIssuedFrom)}
                />
              </div>
              <div className="w-full sm:w-48">
                <DateField
                  label={t('reports.filters.issuedTo')}
                  value={issuedTo}
                  onChange={resetPage(setIssuedTo)}
                />
              </div>
            </FilterBar>
          }
        >
          {registerIsEmpty ? (
            <div className="p-4">
              <EmptyState
                title={t('reports.empty')}
                description={t('reports.emptyHint')}
                action={prepareButton}
              />
            </div>
          ) : (
            <DataTable
              label={t('reports.title')}
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              isLoading={list.isLoading}
              error={list.error}
              onRetry={() => void list.refetch()}
              emptyContent={t('reports.emptyFiltered')}
              page={page}
              totalPages={list.data?.meta.totalPages ?? 1}
              onPageChange={setPage}
            />
          )}
        </RegisterPanel>
      </div>
    </>
  );
}

/**
 * Staff-side indicators. Each is a fact to look at; none changes a status, and
 * the payment hold is never shown anywhere outside the laboratory's own staff.
 */
export function RowIndicators({ row }: { readonly row: ReportRegisterItemView }) {
  const { t } = useTranslation();
  if (!row.hasDraft && row.paymentHold !== 'held' && !row.resultCorrectionAttention) return null;
  return (
    <span className="flex flex-wrap items-center gap-inline">
      {row.hasDraft ? (
        <span title={t('reports.draftIndicatorHint')}>
          <StatusBadge label={t('reports.draftIndicator')} tone="neutral" />
        </span>
      ) : null}
      {row.paymentHold === 'held' ? (
        <span title={t('reports.holdIndicatorHint')}>
          <StatusBadge label={t('reports.holdIndicator')} tone="warning" />
        </span>
      ) : null}
      {row.resultCorrectionAttention ? (
        <span title={t('reports.attentionIndicatorHint')}>
          <StatusBadge label={t('reports.attentionIndicator')} tone="warning" />
        </span>
      ) : null}
    </span>
  );
}
